import { ApplicationInsights } from '@microsoft/applicationinsights-web';

import { installAppInsightsIngestionGuard, setAppInsightsIngestionAllowed } from './appInsightsIngestionGuard';
import { containsBenignBrowserNoise } from './benignBrowserErrors';

installAppInsightsIngestionGuard();
setAppInsightsIngestionAllowed(false);

// tslint:disable-next-line: no-var-requires
const { reactAI } = require('react-appinsights');

export interface IAppInsightsTrackingParams {
    trackingEnabled: boolean;
    platformConfigurationLoaded: boolean;
    instrumentationKey?: string;
}

let appInsightsInstance: ApplicationInsights | null = null;

const reactAINoopInsights = {
    trackMetric: () => undefined,
    trackPageView: () => undefined
};

export const shouldInitializeAppInsights = ({
    trackingEnabled,
    platformConfigurationLoaded,
    instrumentationKey
}: IAppInsightsTrackingParams): boolean =>
    platformConfigurationLoaded
    && trackingEnabled
    && !!instrumentationKey;

const installReactAIUnmountStub = (): void => {
    reactAI.appInsights = reactAINoopInsights;
};

installReactAIUnmountStub();

export const stopAppInsightsTracking = (): void => {
    setAppInsightsIngestionAllowed(false);

    if (appInsightsInstance) {
        const instance = appInsightsInstance;
        appInsightsInstance = null;

        try {
            instance.config.disableTelemetry = true;
            instance.config.disableExceptionTracking = true;
            instance.unload(false);
        } catch {
            // Plugins may not support teardown; the instance is already dropped so no further telemetry is started by us.
        }
    }

    // withAITracking throws on unmount when ReactAI was never initialized (tracking off / HMR remount).
    installReactAIUnmountStub();
};

export const syncAppInsightsTracking = (params: IAppInsightsTrackingParams): void => {
    if (!shouldInitializeAppInsights(params)) {
        stopAppInsightsTracking();
        return;
    }

    if (appInsightsInstance) {
        setAppInsightsIngestionAllowed(true);
        return;
    }

    setAppInsightsIngestionAllowed(true);

    const appInsights = new ApplicationInsights({
        config: {
            instrumentationKey: params.instrumentationKey,
            extensions: [reactAI],
            disableTelemetry: false,
            disableExceptionTracking: false,
            extensionConfig: {
                [reactAI.extensionId]: { history }
            }
        }
    });

    appInsights.loadAppInsights();
    appInsights.addTelemetryInitializer(item => {
        if (containsBenignBrowserNoise(item)) {
            return false;
        }

        return true;
    });

    appInsightsInstance = appInsights;
};
